import React, { useEffect, useMemo, useState } from 'react';
import {
  BarList, Btn, Chip, DataTable, KPI, KPIRow, Loading, Msg, Section, Select, TextInput,
  fmtDate, panelGet, useAdminGet,
} from '../kit';
import {
  BandLegend, RowClick, STEP_LABELS, STEP_ORDER, STEP_PARENT, StackedBar, copiedSteps, fmtMs, fmtPct, num,
  stepParent, toBands,
} from './StepTimings';
import TechCardModal from './TechCardModal';
import { presetRange } from '../period';
import {
  EMPTY_TAG_FILTER, LIFECYCLE_LABEL, TagFilter, TagFilterValue, UserTagCells, describeTagFilter,
  tagFilterActive, tagFilterParams, tagText, toLifecycle, useTagOptions,
} from '../tags/shared';

/**
 * Query explorer — every /search/v2 call, with its step breakdown and the
 * results the user was shown.
 *
 * Backed by admin_search_explorer.py over search_query_logs, which the engine
 * writes for every call (Backend/search_v2/engine.py _log). The list is paged
 * and sorted on the server; the bars and the "by user" list honour the same
 * filters, so clicking one narrows the other; the KPI row is fixed windows
 * whatever the filter says (like the Audit tab). Opening a row fetches the
 * whole entry — ai_* fields, relevance_*, the normalised and raw timings and
 * raw_results joined to the corpus — and a click on a result opens the full
 * technology card. Filter, sort and page live in component state only: the
 * admin router has no per-tab URLs and this tab does not start one.
 */

type Filters = {
  q: string; family: string; route: string; engine: string; backend: string; user: string;
  state: string; results: string; errors: string; cached: string;
  min_ms: string; max_ms: string; min_results: string; max_results: string; from: string; to: string;
};

const EMPTY: Filters = {
  q: '', family: '', route: '', engine: '', backend: '', user: '', state: '', results: '',
  errors: '', cached: '', min_ms: '', max_ms: '', min_results: '', max_results: '', from: '', to: '',
};

// Quick date ranges over the from / to inputs (owner's sheet: "Last 7 days,
// 30 days, 90 days, Lifetime, Custom range"). A preset just fills the two
// dates (UTC days, both included); 'custom' is whatever the inputs hold.
const RANGES = ['all', '7d', '30d', '90d', 'custom'];
const RANGE_LABELS: Record<string, string> = {
  all: 'Lifetime', '7d': 'Last 7 days', '30d': 'Last 30 days', '90d': 'Last 90 days', custom: 'Custom range',
};
const RANGE_DAYS: Record<string, number | null> = { '7d': 7, '30d': 30, '90d': 90, all: null };
const rangeOf = (from: string, to: string): string => {
  if (!from && !to) return 'all';
  for (const k of ['7d', '30d', '90d']) {
    const r = presetRange(k, RANGE_DAYS);
    if (r.from === from && r.to === to) return k;
  }
  return 'custom';
};
// "By user (top N)": how many users the bar list shows.
const USER_LIMITS = ['10', '20', '30', '50', '100'];

const FAMILIES = ['', 'v2', 'cache', 'hybrid'];
const FAMILY_LABELS: Record<string, string> = {
  '': 'Any family', v2: 'v2', cache: 'Cache hits', hybrid: 'Legacy hybrid',
};
const ROUTES = ['', 'typesense', 'engine', 'sql', 'deep', 'filter_browse', 'hybrid'];
const STATES = ['', 'logged_in', 'anonymous'];
const STATE_LABELS: Record<string, string> = { '': 'Any user state', logged_in: 'Logged in', anonymous: 'Anonymous' };
const RESULTS = ['', 'with', 'none'];
const RESULTS_LABELS: Record<string, string> = { '': 'Any result count', with: 'With results', none: 'No results' };
const ERRORS = ['', 'exclude', 'only'];
const ERRORS_LABELS: Record<string, string> = { '': 'Errors: any', exclude: 'Errors: exclude', only: 'Errors only' };
const CACHED = ['', 'exclude', 'only'];
const CACHED_LABELS: Record<string, string> = { '': 'Cache hits: any', exclude: 'Cache hits: exclude', only: 'Cache hits only' };
const FILTER_LABELS: Record<string, string> = {
  q: 'search', family: 'family', route: 'route', engine: 'engine', backend: 'backend', user: 'user',
  state: 'state', results: 'results', errors: 'errors', cached: 'cached', min_ms: 'min ms',
  max_ms: 'max ms', min_results: 'min results', max_results: 'max results', from: 'from', to: 'to',
};

/** The server's whitelist — the browser names one of these, nothing else. */
const SORTS = [
  'created_at', 'total_ms', 'total_results', 'query', 'backend', 'retrieval_mode', 'understanding_ms',
  'classify_ms', 'filters_ms', 'expand_ms', 'retrieve_ms', 'typesense_ms', 'pinecone_ms', 'hydrate_ms',
  'rescore_ms', 'entitlements_ms',
];
const SORT_LABELS: Record<string, string> = {
  created_at: 'When', total_ms: 'Total ms', total_results: 'Results', query: 'Query',
  backend: 'Backend', retrieval_mode: 'Route / engine', understanding_ms: 'Understanding ms',
  classify_ms: 'Classify ms', filters_ms: 'Filters ms', expand_ms: 'Expand ms',
  retrieve_ms: 'Retrieve ms', typesense_ms: 'Typesense ms', pinecone_ms: 'Pinecone ms',
  hydrate_ms: 'Hydrate ms', rescore_ms: 'Rescore ms', entitlements_ms: 'Entitlements ms',
};
const BASE_COLS = ['created_at', 'user_email', 'lifecycle_tags', 'query', 'retrieval_mode', 'total_results', 'total_ms', 'timing'];
/** Optional per-step columns: every one is a whitelisted server sort, so
 *  showing them makes each header a sort control for that step. */
const STEP_COLS = ['entitlements_ms', 'understanding_ms', 'classify_ms', 'filters_ms', 'expand_ms',
  'retrieve_ms', 'typesense_ms', 'pinecone_ms', 'rescore_ms', 'hydrate_ms'];
const LABELS: Record<string, string> = {
  created_at: 'When', user_email: 'User', lifecycle_tags: 'Lifecycle · tags', query: 'Query', retrieval_mode: 'Route / engine',
  total_results: 'Results', total_ms: 'Total', timing: 'Breakdown',
  ...Object.fromEntries(STEP_COLS.map(s => [s, STEP_LABELS[s]])),
};

const iso = (d: Date) => d.toISOString().slice(0, 10);

/** The searcher's lifecycle + tags as text (user_tags.decorate on the row's
 *  user id / e-mail) for the table's search, column filter and CSV. */
const lifecycleText = (r: any) => {
  const lc = toLifecycle(r?.lifecycle);
  return [r?.lifecycle_label || LIFECYCLE_LABEL[lc] || lc, tagText(r)].filter(Boolean).join(' · ');
};

/** Keeps ILIKE-heavy searches off every keystroke. */
function useDebounced(value: string, msDelay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), msDelay);
    return () => clearTimeout(t);
  }, [value, msDelay]);
  return v;
}

const Pill: React.FC<{ tone: 'ok' | 'warn' | 'bad' | 'mute' | 'info'; children: React.ReactNode; title?: string }> =
  ({ tone, children, title }) => {
  const look = tone === 'ok' ? 'text-green-700 bg-green-50 border-green-200'
    : tone === 'warn' ? 'text-amber-700 bg-amber-50 border-amber-200'
      : tone === 'bad' ? 'text-red-600 bg-red-50 border-red-200'
        : tone === 'info' ? 'text-secondary-500 bg-secondary-100 border-secondary-400'
          : 'text-grayscale-1200 bg-grayscale-200 border-grayscale-300';
  return <span title={title} className={`text-[11px] rounded-full border px-2 py-0.5 whitespace-nowrap ${look}`}>{children}</span>;
};

const familyPill = (fam: any) => {
  const v = String(fam || '?');
  return <Pill tone={v === 'v2' ? 'ok' : v === 'cache' ? 'mute' : v === 'hybrid' ? 'warn' : 'mute'}>{v}</Pill>;
};

const Line: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex justify-between gap-4 text-sm border-b border-grayscale-200 py-1 last:border-0">
    <span className="text-grayscale-1100 whitespace-nowrap">{label}</span>
    <span className="text-grayscale-1300 text-right break-words min-w-0">
      {value === null || value === undefined || value === '' ? '—' : value}
    </span>
  </div>
);

const yesNo = (v: any) => v === true ? 'yes' : v === false ? 'no' : v == null ? '' : String(v);

const jsonText = (v: any): string => {
  try { return JSON.stringify(v ?? null, null, 2); } catch { return String(v); }
};
const isEmptyish = (v: any) =>
  v === null || v === undefined || v === '' ||
  (Array.isArray(v) && v.length === 0) ||
  (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0);

const Json: React.FC<{ value: any; maxH?: string }> = ({ value, maxH = '14rem' }) => (
  isEmptyish(value)
    ? <div className="text-xs text-grayscale-1100 px-1">—</div>
    : <pre className="text-xs bg-grayscale-100 border border-grayscale-300 rounded-lg p-2 overflow-auto whitespace-pre-wrap break-all"
        style={{ maxHeight: maxH }}>{jsonText(value)}</pre>
);

const Collapse: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex flex-col gap-1">
      <button type="button" onClick={() => setOpen(o => !o)}
        className="text-xs text-grayscale-1200 underline underline-offset-2 text-left w-fit">
        {open ? '▾' : '▸'} {title}
      </button>
      {open && children}
    </div>
  );
};

/** The copied steps of a cache hit describe the run that filled the cache,
 *  whose total was not logged; the best base for their percentages is the
 *  sum of its phases (retrieve left out when it ran inside understanding). */
const copiedBase = (t: Record<string, any>, copied: string[]): number | null => {
  const phases = ['understanding_ms', 'retrieve_ms', 'rescore_ms', 'hydrate_ms']
    .filter(s => copied.includes(s) && !stepParent(s, t));
  const sum = phases.reduce((a, s) => a + (num(t[s]) ?? 0), 0);
  return sum > 0 ? sum : null;
};

/** One line per step for a title tooltip — every step, not just the bands. */
const stepsTitle = (t: Record<string, any> | null | undefined): string => {
  if (!t) return '';
  const total = num(t.total_ms);
  const copied = copiedSteps(t);
  const base = copiedBase(t, copied);
  return STEP_ORDER.filter(s => num(t[s]) !== null)
    .map(s => {
      const pct = s === 'total_ms' ? '' : ` (${fmtPct(t[s], copied.includes(s) ? base : total)}${copied.includes(s) ? ' of the original run' : ''})`;
      return `${stepParent(s, t) ? '   ' : ''}${STEP_LABELS[s]}: ${fmtMs(t[s], 1)}${pct}`;
    })
    .join('\n')
    + (t.retrieve_inside_understanding ? '\n(retrieve ran inside understanding: speculative Typesense)' : '');
};

const rowBands = (r: any) => toBands(s => num(r.timings?.[s]), num(r.total_ms ?? r.timings?.total_ms), r.timings);

/** Cache hits carry the original run's step timings; the server flags them.
 *  Either spelling — on the timings dict or on the row — is honoured. */
const fromOriginalRun = (r: any): boolean =>
  !!(r?.timings?.from_original_run || r?.from_original_run || (r?.cached && r?.timings));

// ---------------------------------------------------------------------------
// Step breakdown — one bar per normalised step, sub-steps indented
// ---------------------------------------------------------------------------
const StepBreakdown: React.FC<{ timings: Record<string, any>; total: number | null; copied: boolean }> =
  ({ timings, total, copied }) => {
  const steps = STEP_ORDER.filter(s => s !== 'total_ms' && num(timings[s]) !== null);
  if (!steps.length) return <div className="text-xs text-grayscale-1100">No step timings on this row.</div>;
  // A cache hit's own steps (total, entitlements, other) are read against
  // its own total; the copied ones against the original run's phase sum —
  // against the hit's 8 ms they would read as thousands of percent.
  const copiedList = copied ? copiedSteps(timings) : [];
  const origBase = copiedBase(timings, copiedList);
  const nested = !!timings.retrieve_inside_understanding;
  const base = Math.max(1, total || 0, origBase || 0, ...steps.map(s => num(timings[s]) || 0));
  return (
    <div className="flex flex-col gap-1">
      {copied && (
        <Pill tone="warn" title={`This was a cache hit: its total, entitlements and other are its own; ${copiedList.map(s => STEP_LABELS[s] || s).join(', ')} were copied from the run that filled the cache and are read against that run's phase sum${origBase ? ` (${fmtMs(origBase, 1)})` : ''}`}>
          {copiedList.length ? `${copiedList.length} steps copied from the original run` : 'steps copied from the original run'}
        </Pill>
      )}
      {nested && (
        <Pill tone="info" title="The speculative Typesense call ran inside understanding and its result was used: retrieve's time is already inside understanding's">
          retrieve ran inside understanding
        </Pill>
      )}
      {steps.map(s => {
        const v = num(timings[s]) || 0;
        const sub = !!stepParent(s, timings);
        const isCopy = copiedList.includes(s);
        return (
          <div key={s} className={`flex items-center gap-2 text-sm ${sub ? 'pl-5' : ''}`}>
            <div className={`w-28 truncate ${sub ? 'text-grayscale-1100 text-xs' : 'text-grayscale-1200'}`}
              title={isCopy ? 'copied from the original run' : undefined}>
              {STEP_LABELS[s]}{isCopy ? <span className="text-amber-700"> *</span> : null}
            </div>
            <div className="flex-1 bg-grayscale-200 rounded-full h-2.5 overflow-hidden">
              <div className={`h-2.5 rounded-full ${isCopy ? 'bg-amber-300' : sub ? 'bg-primary-300' : 'bg-primary-500'}`}
                style={{ width: `${Math.min(100, (Math.max(0, v) / base) * 100)}%` }} />
            </div>
            <div className={`w-24 text-right ${v < 0 ? 'text-amber-700' : 'text-grayscale-1300'}`}
              title={v < 0 ? 'negative: the phases overlap by this much' : undefined}>{fmtMs(v, 1)}</div>
            <div className="w-12 text-right text-xs text-grayscale-1100" title={isCopy ? 'of the original run’s phase sum' : undefined}>
              {fmtPct(v, isCopy ? origBase : total)}
            </div>
          </div>
        );
      })}
      {copiedList.length > 0 && <div className="text-[11px] text-grayscale-1100">* copied from the original run; % of that run's phase sum</div>}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Query drawer — one entry in full, results included
// ---------------------------------------------------------------------------
const RESULT_COLS = ['rank', 'percentage', 'title', 'university_name', 'university_country',
  'calculated_trl', 'found_by', 'hidden_by_threshold'];
const RESULT_LABELS: Record<string, string> = {
  rank: '#', percentage: 'Score', title: 'Title', university_name: 'University',
  university_country: 'Country', calculated_trl: 'TRL', found_by: 'Found by', hidden_by_threshold: 'Hidden',
};
const RESULT_SORTS = ['rank', 'score', 'trl'];
const RESULT_SORT_LABELS: Record<string, string> = { rank: 'Sort: rank', score: 'Sort: score', trl: 'Sort: TRL' };

const trlNum = (v: any): number | null => {
  const m = String(v ?? '').match(/\d+/);
  return m ? Number(m[0]) : null;
};
/** The server's score_rule: which number the card showed for a result.
 *  llm = the judge's 0-100; rerank = the cross-encoder's score (legacy
 *  hybrid rows before the LLM rescore); match = the SQL keyword route's
 *  match score (min(1, match/30)); rank = position-based, 100% for the first
 *  hit down to 0. Anything unknown reads as rank. */
const RULE_TONE: Record<string, 'ok' | 'info' | 'mute'> = { llm: 'ok', rerank: 'info', match: 'info', rank: 'mute' };
const ruleTitle = (r: any): string => {
  const rule = String(r?.score_rule || '');
  if (rule === 'llm') return `LLM relevance ${r.llm_relevance ?? ''}${r.llm_reason ? ` — ${r.llm_reason}` : ''}`;
  if (rule === 'rerank') return `cross-encoder rerank score ${r.rerank_score ?? ''}`;
  if (rule === 'match') return `SQL keyword match score ${r.rrf_score ?? ''} (matches / 30, capped at 1)`;
  return 'position-based';
};
const legsOf = (r: any): string[] =>
  Array.isArray(r?.found_by) ? r.found_by.map(String) : r?.found_by ? String(r.found_by).split('+') : [];

const QueryDrawer: React.FC<{
  id: string; onClose: () => void; onOpen: (id: string) => void; onSearch: (q: string) => void;
}> = ({ id, onClose, onOpen, onSearch }) => {
  const [entry, setEntry] = useState<any>(null);
  const [error, setError] = useState('');
  const [similar, setSimilar] = useState<any[]>([]);
  const [copied, setCopied] = useState(false);
  const [techId, setTechId] = useState<string | null>(null);
  // Result controls
  const [minScore, setMinScore] = useState('0');
  const [legs, setLegs] = useState<string[]>([]);
  const [text, setText] = useState('');
  const [sortBy, setSortBy] = useState('rank');
  const [hideHidden, setHideHidden] = useState(false);

  useEffect(() => {
    let alive = true;
    setEntry(null); setError(''); setSimilar([]); setTechId(null);
    setMinScore('0'); setLegs([]); setText(''); setSortBy('rank'); setHideHidden(false);
    panelGet('search', 'explorer_entry', { id }).then(({ data, error }) => {
      if (!alive) return;
      setEntry(data); setError(error);
    });
    return () => { alive = false; };
  }, [id]);

  const query: string = entry?.query || '';
  useEffect(() => {
    if (!query) return;
    let alive = true;
    panelGet('search', 'explorer_similar', { q: query }).then(({ data }) => {
      if (!alive) return;
      // {rows: [{query, n, last_at, last_id, exact, avg_ms, avg_results, errors, cached}]}
      const items = Array.isArray(data?.rows) ? data.rows : [];
      setSimilar(items.filter((s: any) => s && s.query));
    });
    return () => { alive = false; };
  }, [query]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(jsonText(entry));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      window.prompt('Copy this:', jsonText(entry));
    }
  };

  const results: any[] = useMemo(() => Array.isArray(entry?.results) ? entry.results : [], [entry]);
  const summary = entry?.results_summary || {};
  const legsAvailable: string[] = useMemo(() => {
    const fromSummary = Object.keys(summary.by_found_by || {});
    if (fromSummary.length) return fromSummary;
    return Array.from(new Set(results.flatMap(legsOf)));
  }, [summary, results]);

  const view = useMemo(() => {
    const min = num(minScore) ?? 0;
    const needle = text.trim().toLowerCase();
    let out = results.filter(r => {
      if (min > 0 && (num(r.percentage) ?? 0) < min) return false;
      if (hideHidden && r.hidden_by_threshold) return false;
      if (legs.length && !legsOf(r).some(l => legs.includes(l))) return false;
      if (needle && !`${r.title || ''} ${r.university_name || ''} ${r.university_country || ''}`.toLowerCase().includes(needle)) return false;
      return true;
    });
    if (sortBy === 'score') {
      out = [...out].sort((a, b) => (num(b.percentage) ?? -1) - (num(a.percentage) ?? -1));
    } else if (sortBy === 'trl') {
      out = [...out].sort((a, b) => (trlNum(b.calculated_trl) ?? -1) - (trlNum(a.calculated_trl) ?? -1));
    } else {
      out = [...out].sort((a, b) => (num(a.rank) ?? 1e9) - (num(b.rank) ?? 1e9));
    }
    return out;
  }, [results, minScore, text, legs, sortBy, hideHidden]);

  const timings: Record<string, any> = entry?.timings || {};
  const totalMs = num(entry?.total_ms ?? timings.total_ms);
  const userLabel = entry?.user_email || (entry?.user_id ? entry.user_id : 'anonymous');
  const buckets: { label: string; value: number }[] =
    (summary.score_buckets || []).map((b: any) => ({ label: String(b.label), value: num(b.n) ?? 0 }));
  const expansion = entry?.expansion ?? entry?.expanded_terms ?? entry?.ai_expansion ?? null;

  return (
    <div className="fixed inset-0 z-40 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" />
      <div className="relative h-full bg-white shadow-xl flex flex-col" style={{ width: 'min(96vw, 72rem)' }}
        onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-grayscale-300 flex items-start justify-between gap-3">
          <div className="min-w-0 flex flex-col gap-1">
            <div className="text-base font-bold text-grayscale-1300 font-grotesk break-words">
              {entry ? (entry.query || <span className="text-grayscale-1100 font-normal">(empty query)</span>) : `Query ${id}`}
            </div>
            {entry && (
              <div className="flex items-center gap-2 flex-wrap text-xs text-grayscale-1100">
                <span title={entry.created_at}>{fmtDate(entry.created_at)}</span>
                <span>·</span>
                <span title={entry.user_id || ''}>{userLabel}</span>
                <span>·</span>
                {familyPill(entry.family)}
                {entry.route && <Pill tone="info">{entry.route}{entry.engine ? ` / ${entry.engine}` : ''}</Pill>}
                {entry.backend && <span className="font-mono">{entry.backend}</span>}
                <span>·</span>
                <span className="font-medium text-grayscale-1300">{fmtMs(totalMs, 1)}</span>
                <span>·</span>
                <span>{num(entry.total_results) !== null ? `${Number(entry.total_results).toLocaleString()} results` : '— results'}</span>
                {entry.cached && <Pill tone="mute">cache hit</Pill>}
                {entry.error && <Pill tone="bad">error</Pill>}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {entry && <Btn small onClick={copy}>{copied ? 'Copied' : 'Copy as JSON'}</Btn>}
            <Btn small onClick={onClose}>Close</Btn>
          </div>
        </div>
        <div className="p-5 overflow-y-auto flex flex-col gap-4">
          {error && <Msg kind="error">{error}</Msg>}
          {!entry && !error && <Loading />}
          {entry && (
            <>
              {entry.error && <Msg kind="error">{String(entry.error)}</Msg>}
              {Array.isArray(entry.notes) && entry.notes.length > 0 && (
                <Msg kind="ok">{entry.notes.map(String).join(' · ')}</Msg>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Section title="Step timings">
                  <StepBreakdown timings={timings} total={totalMs} copied={fromOriginalRun(entry)} />
                  <Collapse title="Raw timings as stored (legacy rows are in seconds)">
                    <Json value={entry.raw_timings} />
                  </Collapse>
                </Section>
                <Section title="Understanding">
                  <div>
                    <Line label="AI understanding used" value={yesNo(entry.ai_understanding_used)} />
                    <Line label="Model" value={entry.ai_model} />
                    <Line label="Search query used" value={entry.ai_search_query} />
                    <Line label="Pure filter query" value={yesNo(entry.ai_is_pure_filter)} />
                    <Line label="Understanding took" value={num(entry.ai_took_ms) !== null ? fmtMs(entry.ai_took_ms, 1) : ''} />
                    <Line label="Retrieval mode" value={entry.retrieval_mode} />
                  </div>
                  {entry.ai_reasoning && (
                    <p className="text-xs text-grayscale-1200 whitespace-pre-wrap">{String(entry.ai_reasoning)}</p>
                  )}
                  {!isEmptyish(expansion) && (
                    <div>
                      <div className="text-xs font-bold text-grayscale-1200 mb-1">Expansion</div>
                      {Array.isArray(expansion)
                        ? <div className="flex gap-1 flex-wrap">{expansion.map((k: any, i: number) => <Pill key={i} tone="mute">{String(k)}</Pill>)}</div>
                        : <Json value={expansion} />}
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <div>
                      <div className="text-xs font-bold text-grayscale-1200 mb-1">Filters requested (UI)</div>
                      <Json value={entry.filters_requested} />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-grayscale-1200 mb-1">Filters extracted (AI)</div>
                      <Json value={entry.ai_filters_extracted} />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-grayscale-1200 mb-1">Filters used (UI wins)</div>
                      <Json value={entry.filters_used} />
                    </div>
                  </div>
                  {!isEmptyish(entry.ai_dropped) && (
                    <Collapse title="Dropped by understanding"><Json value={entry.ai_dropped} /></Collapse>
                  )}
                  {entry.ai_raw_response && (
                    <Collapse title="Raw model response">
                      <pre className="text-xs bg-grayscale-100 border border-grayscale-300 rounded-lg p-2 overflow-auto max-h-64 whitespace-pre-wrap break-all">
                        {String(entry.ai_raw_response)}
                      </pre>
                    </Collapse>
                  )}
                </Section>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Section title="Retrieval">
                  <div>
                    <Line label="Typesense returned" value={entry.typesense_returned} />
                    <Line label="Pinecone returned" value={entry.pinecone_returned} />
                    <Line label="Unique after dedupe" value={entry.unique_after_dedupe} />
                    <Line label="Found by both" value={entry.found_by_both} />
                    <Line label="Typesense" value={num(entry.typesense_ms) !== null ? fmtMs(entry.typesense_ms, 1) : ''} />
                    <Line label="Pinecone" value={num(entry.pinecone_ms) !== null ? fmtMs(entry.pinecone_ms, 1) : ''} />
                    <Line label="Retrieval" value={num(entry.retrieval_ms) !== null ? fmtMs(entry.retrieval_ms, 1) : ''} />
                  </div>
                </Section>
                <Section title="Relevance (LLM rescore)">
                  <div>
                    <Line label="Triggered" value={yesNo(entry.relevance_triggered)} />
                    <Line label="Model" value={entry.relevance_model} />
                    <Line label="Reason" value={entry.relevance_reason} />
                    <Line label="Max rerank" value={entry.relevance_max_rerank} />
                    <Line label="Scored" value={entry.relevance_scored} />
                    <Line label="Reordered" value={yesNo(entry.relevance_reordered)} />
                    <Line label="Took" value={num(entry.relevance_ms) !== null ? fmtMs(entry.relevance_ms, 1) : ''} />
                  </div>
                  {entry.relevance_raw && (
                    <Collapse title="Raw rescore response">
                      <pre className="text-xs bg-grayscale-100 border border-grayscale-300 rounded-lg p-2 overflow-auto max-h-64 whitespace-pre-wrap break-all">
                        {String(entry.relevance_raw)}
                      </pre>
                    </Collapse>
                  )}
                </Section>
              </div>

              <Section title={`Results shown to the user (${results.length}${num(entry.total_results) !== null && Number(entry.total_results) !== results.length ? ` of ${Number(entry.total_results).toLocaleString()} counted` : ''})`}>
                <KPIRow>
                  <KPI label="Logged" value={(num(summary.n) ?? results.length).toLocaleString()} hint="raw_results keeps up to 250" />
                  <KPI label="Shown" value={(num(summary.shown) ?? results.filter(r => !r.hidden_by_threshold).length).toLocaleString()} />
                  <KPI label="Hidden by threshold" value={(num(summary.hidden_by_threshold) ?? results.filter(r => r.hidden_by_threshold).length).toLocaleString()}
                    onClick={() => setHideHidden(h => !h)} active={hideHidden} hint={hideHidden ? 'hidden rows excluded' : 'click to exclude'} />
                  <KPI label="With LLM score" value={(num(summary.with_llm_score) ?? results.filter(r => num(r.llm_relevance) !== null).length).toLocaleString()}
                    hint="rest: rerank / match / rank" />
                  {num(summary.in_corpus) !== null && Number(summary.in_corpus) !== results.length && (
                    <KPI label="Still in corpus" value={Number(summary.in_corpus).toLocaleString()}
                      hint={`${results.length - Number(summary.in_corpus)} since removed`} />
                  )}
                  {legsAvailable.map(l => (
                    <KPI key={l} label={`Found by ${l}`}
                      value={(num(summary.by_found_by?.[l]) ?? results.filter(r => legsOf(r).includes(l)).length).toLocaleString()}
                      onClick={() => setLegs(ls => ls.includes(l) ? ls.filter(x => x !== l) : [...ls, l])}
                      active={legs.includes(l)} />
                  ))}
                </KPIRow>
                {buckets.length > 0 && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs text-grayscale-1100 mb-1">Score buckets — click one to set the minimum</div>
                      <BarList items={buckets}
                        onSelect={l => { const lo = String(l).match(/\d+/); setMinScore(s => lo && s !== lo[0] ? lo[0] : '0'); }}
                        activeLabel={buckets.find(b => (b.label.match(/\d+/) || [''])[0] === minScore && minScore !== '0')?.label} />
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-2 flex-wrap">
                  <TextInput value={text} onChange={setText} placeholder="Filter by title, university, country…" wide />
                  <span className="text-xs text-grayscale-1100">min score</span>
                  <input type="range" min={0} max={100} step={1} value={num(minScore) ?? 0}
                    onChange={e => setMinScore(e.target.value)} className="w-32" title={`≥ ${minScore}%`} />
                  <input type="number" min={0} max={100} value={minScore}
                    onChange={e => setMinScore(e.target.value)}
                    className="h-9 w-20 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm" />
                  <span className="text-xs text-grayscale-1100">%</span>
                  <Select value={sortBy} onChange={setSortBy} options={RESULT_SORTS} labels={RESULT_SORT_LABELS} />
                  <label className="text-xs text-grayscale-1200 inline-flex items-center gap-1">
                    <input type="checkbox" checked={hideHidden} onChange={e => setHideHidden(e.target.checked)} />
                    hide threshold-hidden
                  </label>
                  {legsAvailable.length > 0 && (
                    <span className="inline-flex items-center gap-1 flex-wrap">
                      <span className="text-xs text-grayscale-1100">found by</span>
                      {legsAvailable.map(l => (
                        <button key={l} type="button"
                          onClick={() => setLegs(ls => ls.includes(l) ? ls.filter(x => x !== l) : [...ls, l])}
                          className={`text-[11px] rounded-full border px-2 py-0.5 ${legs.includes(l)
                            ? 'bg-primary-500 text-grayscale-100 border-primary-500'
                            : 'border-grayscale-400 text-grayscale-1200 hover:bg-grayscale-200'}`}>
                          {l}
                        </button>
                      ))}
                    </span>
                  )}
                  <span className="text-xs text-grayscale-1100">{view.length} of {results.length}</span>
                  {(text || legs.length || (num(minScore) ?? 0) > 0 || hideHidden || sortBy !== 'rank') && (
                    <button type="button" className="text-xs text-grayscale-1100 underline underline-offset-2"
                      onClick={() => { setText(''); setLegs([]); setMinScore('0'); setHideHidden(false); setSortBy('rank'); }}>
                      reset
                    </button>
                  )}
                </div>
                <p className="text-xs text-grayscale-1100">
                  Score is what the product displayed: the LLM relevance when the rescore ran (badge
                  "llm"); on legacy hybrid rows the cross-encoder's rerank score ("rerank"); on the SQL
                  keyword route the match score, matches / 30 capped at 100% ("match"); otherwise
                  position-based, 100% for the first hit down to 0 ("rank"). Hover a badge for the
                  number behind it. Click a row for the full technology card.
                </p>
                <RowClick onRow={setTechId}>
                  <DataTable rows={view} columns={RESULT_COLS} labels={RESULT_LABELS}
                    csvName={`query_${id}_results`} maxHeight="30rem"
                    empty={results.length ? 'No result matches these controls.' : 'No results were logged for this query.'}
                    render={{
                      rank: r => <span data-row-key={r.utech_technology_id} className="text-grayscale-1100">{r.rank ?? '—'}</span>,
                      percentage: r => (
                        <span className="inline-flex items-center gap-1.5">
                          <span className="font-medium">{num(r.percentage) !== null ? `${Number(r.percentage).toFixed(1)}%` : '—'}</span>
                          {r.score_rule && <Pill tone={RULE_TONE[String(r.score_rule)] || 'mute'} title={ruleTitle(r)}>
                            {String(r.score_rule)}
                          </Pill>}
                        </span>
                      ),
                      title: r => (
                        <span className={`inline-flex items-center gap-1.5 ${r.in_corpus === false ? 'text-grayscale-1100' : ''}`}
                          title={r.llm_reason ? String(r.llm_reason) : r.title}>
                          <span className="truncate max-w-[22rem] inline-block align-bottom">{r.title || r.utech_technology_id}</span>
                          {r.in_corpus === false && <Pill tone="bad" title="This technology is no longer in the corpus">gone</Pill>}
                        </span>
                      ),
                      calculated_trl: r => r.calculated_trl == null || r.calculated_trl === '' ? '—' : String(r.calculated_trl),
                      found_by: r => (
                        <span className="inline-flex gap-1 flex-wrap">
                          {legsOf(r).length ? legsOf(r).map(l => <Pill key={l} tone="mute">{l}</Pill>) : '—'}
                        </span>
                      ),
                      hidden_by_threshold: r => r.hidden_by_threshold ? <Pill tone="warn">hidden</Pill> : '',
                    }} />
                </RowClick>
              </Section>

              <Section title={`Similar earlier queries${similar.length ? ` (${similar.length})` : ''}`}>
                {!similar.length ? (
                  <div className="text-xs text-grayscale-1100">No other logged query contains this text.</div>
                ) : (
                  <div className="flex flex-col gap-1 max-h-72 overflow-auto">
                    {similar.map((s: any, i: number) => (
                      <div key={i}
                        className="flex items-center gap-2 text-sm rounded-md px-2 py-1 border border-transparent hover:bg-primary-50 hover:border-primary-300">
                        <button type="button" title={s.last_id ? 'Open the latest run of this query' : undefined}
                          disabled={!s.last_id} onClick={() => s.last_id && onOpen(String(s.last_id))}
                          className="flex-1 min-w-0 flex items-center gap-2 text-left disabled:cursor-default">
                          <span className="min-w-0 truncate text-grayscale-1300">{s.query}</span>
                          {s.exact && <Pill tone="ok" title="The same words as this query">same</Pill>}
                          {num(s.errors) ? <Pill tone="bad">{s.errors} err</Pill> : null}
                        </button>
                        <span className="text-xs text-grayscale-1100 whitespace-nowrap">
                          {num(s.n) !== null ? `${s.n}×` : ''} · {fmtDate(s.last_at)} · {fmtMs(s.avg_ms)}
                          {num(s.avg_results) !== null ? ` · ${Number(s.avg_results).toLocaleString(undefined, { maximumFractionDigits: 1 })} results` : ''}
                        </span>
                        <Btn small title="Close this and list every run of this query" onClick={() => onSearch(String(s.query))}>
                          all runs
                        </Btn>
                      </div>
                    ))}
                  </div>
                )}
              </Section>

              <Collapse title="Whole entry as JSON">
                <Json value={entry} maxH="60vh" />
              </Collapse>
            </>
          )}
        </div>
        {techId && (
          <TechCardModal id={techId} onClose={() => setTechId(null)}
            onOpenQuery={qid => { setTechId(null); onOpen(qid); }} />
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// The tab
// ---------------------------------------------------------------------------
const QueryExplorer: React.FC = () => {
  const [f, setF] = useState<Filters>(EMPTY);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState('50');
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(null);
  const [showSteps, setShowSteps] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [userLimit, setUserLimit] = useState('20');
  const q = useDebounced(f.q);
  const user = useDebounced(f.user);
  const minMs = useDebounced(f.min_ms); const maxMs = useDebounced(f.max_ms);
  const minResults = useDebounced(f.min_results); const maxResults = useDebounced(f.max_results);
  // Lifecycle + custom tags of the SEARCHER (standard params, server-side —
  // list and bars alike). Anonymous queries match only an empty tag filter.
  const [tagF, setTagF] = useState<TagFilterValue>(EMPTY_TAG_FILTER);
  const tagOpts = useTagOptions();

  const set = (k: keyof Filters) => (v: string) => setF(s => ({ ...s, [k]: v }));
  // A toggle: clicking the bar or KPI that is already filtering clears it.
  const toggle = (k: keyof Filters, v: string) => setF(s => ({ ...s, [k]: s[k] === v ? '' : v }));

  const filterParams = useMemo(() => ({
    q, family: f.family, route: f.route, engine: f.engine, backend: f.backend, user,
    state: f.state, results: f.results, errors: f.errors, cached: f.cached,
    min_ms: minMs, max_ms: maxMs, min_results: minResults, max_results: maxResults,
    from: f.from, to: f.to,
  }), [q, f.family, f.route, f.engine, f.backend, user, f.state, f.results, f.errors, f.cached,
       minMs, maxMs, minResults, maxResults, f.from, f.to]);
  // Kept apart from filterParams: its keys are the chip/clear vocabulary of
  // `f`, while the tag filter has one chip of its own.
  const queryParams = useMemo(() => ({ ...filterParams, ...tagFilterParams(tagF) }), [filterParams, tagF]);
  const filterKey = JSON.stringify(queryParams);

  // A new filter or sort starts again from the first page.
  useEffect(() => { setPage(0); }, [filterKey, perPage, sort]);

  const facets = useAdminGet('search', 'explorer_facets', { ...queryParams, user_limit: userLimit },
    [filterKey, tick, userLimit]);
  const range = rangeOf(f.from, f.to);
  const pickRange = (k: string) => {
    if (k === 'custom') {
      // Custom keeps the dates in the inputs; from nothing it starts at the last 30 days.
      if (!f.from && !f.to) setF(s => ({ ...s, ...presetRange('30d', RANGE_DAYS) }));
      return;
    }
    const r = presetRange(k, RANGE_DAYS);
    setF(s => ({ ...s, from: r.from || '', to: r.to || '' }));
  };
  const list = useAdminGet('search', 'explorer_list',
    { ...queryParams, page, per_page: perPage, sort: sort?.key || '', dir: sort?.dir || '' },
    [filterKey, page, perPage, sort, tick]);

  const rows = useMemo(() => (list.data?.rows || []).map((r: any) => {
    // Step columns are flattened onto the row so DataTable can show and the
    // header can sort them; the row's own total_ms is left alone.
    const flat: Record<string, any> = { ...r, timing: '', lifecycle_tags: lifecycleText(r) };
    for (const s of STEP_COLS) flat[s] = num(r.timings?.[s]);
    return flat;
  }), [list.data]);
  const total: number = list.data?.total ?? 0;
  const pages: number = list.data?.pages ?? 1;
  const kpis = facets.data?.kpis || {};
  const facetValues = (k: string): string[] =>
    (facets.data?.[k] || []).map((a: any) => String(a.value ?? a.email ?? '')).filter(Boolean);
  const withCurrent = (fixed: string[], seen: string[], current: string) =>
    Array.from(new Set(['', ...fixed, ...(current ? [current] : []), ...seen]));
  const routes = withCurrent(ROUTES.slice(1), facetValues('routes'), f.route);
  const engines = withCurrent([], facetValues('engines'), f.engine);
  const backends = withCurrent([], facetValues('backends'), f.backend);
  const active = Object.entries(filterParams).filter(([, v]) => v);
  const tagActive = tagFilterActive(tagF);
  const nActive = active.length + (tagActive ? 1 : 0);
  const today = iso(new Date());
  const cacheRate = num(kpis.cache_rate_week);

  const onSortChange = (key: string, dir: 'asc' | 'desc' | '') => {
    if (!key || !dir) { setSort(null); return; }
    // Only the server's whitelist is sortable; a click on any other header is a no-op.
    if (SORTS.includes(key)) setSort({ key, dir });
  };
  const columns = showSteps ? [...BASE_COLS, ...STEP_COLS] : BASE_COLS;

  // A facet bar keeps its raw value beside its label: the label is what the
  // admin reads ('(none)' for an empty value), the raw value is what the
  // filter sends — an empty one has no filter to send, so its bar is inert.
  // The one exception is the engine: no engine means a legacy hybrid row,
  // and that IS a filter (family=hybrid), so that bar reads 'legacy'.
  type FacetItem = { label: string; value: number; raw: string };
  const facetItems = (k: string, emptyLabel = '(none)'): FacetItem[] =>
    (facets.data?.[k] || []).map((a: any) => {
      const raw = String(a.value ?? a.email ?? '');
      return { label: raw || emptyLabel, value: num(a.n) ?? 0, raw };
    });
  const rawOf = (items: FacetItem[], label: string) => items.find(i => i.label === label)?.raw ?? '';
  const routeItems = facetItems('routes');
  const engineItems = facetItems('engines', 'legacy');
  const userItems = facetItems('users', '(anonymous)');
  const onEngineBar = (label: string) => {
    const raw = rawOf(engineItems, label);
    if (raw) toggle('engine', raw);
    else if (label === 'legacy') toggle('family', 'hybrid');
  };

  return (
    <div className="flex flex-col gap-4">
      <KPIRow>
        <KPI label="All queries" value={(kpis.total ?? 0).toLocaleString()} />
        <KPI label="Today" value={(kpis.today ?? 0).toLocaleString()}
          onClick={() => setF(s => s.from === today && !s.to ? { ...s, from: '' } : { ...s, from: today, to: '' })}
          active={f.from === today && !f.to} hint="click to show them" />
        <KPI label="This week" value={(kpis.week ?? 0).toLocaleString()} hint="last 7 days" />
        <KPI label="p50 latency" value={fmtMs(kpis.p50_ms_week)} hint="last 7 days, cache hits excluded" />
        <KPI label="p95 latency" value={fmtMs(kpis.p95_ms_week)} hint="last 7 days" />
        <KPI label="Cache rate" value={cacheRate === null ? '—' : `${Math.round(cacheRate * 100)}%`}
          onClick={() => toggle('cached', 'only')} active={f.cached === 'only'} hint="last 7 days · click for hits" />
        <KPI label="Errors" value={(kpis.errors_week ?? 0).toLocaleString()}
          onClick={() => toggle('errors', 'only')} active={f.errors === 'only'} hint="last 7 days · click to show" />
        <KPI label="Avg results" value={num(kpis.avg_results_week) === null ? '—' : Number(kpis.avg_results_week).toLocaleString(undefined, { maximumFractionDigits: 1 })}
          hint="last 7 days" />
      </KPIRow>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4">
        <Section title="By route">
          {facets.loading && !facets.data ? <Loading /> : (
            <BarList items={routeItems.map(({ label, value }) => ({ label, value }))}
              onSelect={l => { const raw = rawOf(routeItems, l); if (raw) toggle('route', raw); }}
              activeLabel={f.route || undefined} />
          )}
        </Section>
        <Section title="By engine">
          {facets.loading && !facets.data ? <Loading /> : (
            <BarList items={engineItems.map(({ label, value }) => ({ label, value }))}
              onSelect={onEngineBar}
              activeLabel={f.engine || (f.family === 'hybrid' ? 'legacy' : undefined)} />
          )}
        </Section>
        <Section title={`By user (top ${userLimit})`}
          right={<Select value={userLimit} onChange={setUserLimit} options={USER_LIMITS}
            labels={Object.fromEntries(USER_LIMITS.map(x => [x, `Top ${x}`]))} />}>
          {facets.loading && !facets.data ? <Loading /> : (
            <div className="max-h-64 overflow-auto">
              <BarList items={userItems.map(({ label, value }) => ({ label, value }))}
                onSelect={l => { const raw = rawOf(userItems, l); if (raw) toggle('user', raw); }}
                activeLabel={f.user || undefined} />
            </div>
          )}
        </Section>
        <Section title="Last 30 days">
          {facets.loading && !facets.data ? <Loading /> : (
            <div className="max-h-64 overflow-auto">
              <BarList items={(facets.data?.days || []).map((d: any) => ({ label: String(d.day), value: num(d.n) ?? 0 }))}
                onSelect={l => setF(s => s.from === l && s.to === l ? { ...s, from: '', to: '' } : { ...s, from: l, to: l })}
                activeLabel={f.from && f.from === f.to ? f.from : undefined} />
            </div>
          )}
        </Section>
      </div>

      <Section title="Queries"
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-grayscale-1100">{total.toLocaleString()} quer{total === 1 ? 'y' : 'ies'}</span>
            {nActive > 0 && (
              <Btn small onClick={() => { setF(EMPTY); setTagF(EMPTY_TAG_FILTER); }}>
                Clear {nActive} filter{nActive === 1 ? '' : 's'}
              </Btn>
            )}
            <Btn small onClick={() => setShowSteps(v => !v)} title="Adds one column per step, each sortable">
              {showSteps ? 'Hide step columns' : 'Show step columns'}
            </Btn>
            <Btn small onClick={() => setTick(t => t + 1)}>Refresh</Btn>
          </div>
        }>
        <div className="flex items-center gap-2 flex-wrap">
          <TextInput value={f.q} onChange={set('q')} wide placeholder="Search query text, the AI's search query or an id…" />
          <Select value={f.family} onChange={set('family')} options={FAMILIES} labels={FAMILY_LABELS} />
          <Select value={f.route} onChange={set('route')} options={routes} labels={{ '': 'Any route' }} />
          <Select value={f.engine} onChange={set('engine')} options={engines} labels={{ '': 'Any engine' }} />
          <Select value={f.backend} onChange={set('backend')} options={backends} labels={{ '': 'Any backend' }} />
          <Select value={f.state} onChange={set('state')} options={STATES} labels={STATE_LABELS} />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <TextInput value={f.user} onChange={set('user')} placeholder="User email or id" />
          <Select value={f.results} onChange={set('results')} options={RESULTS} labels={RESULTS_LABELS} />
          <Select value={f.errors} onChange={set('errors')} options={ERRORS} labels={ERRORS_LABELS} />
          <Select value={f.cached} onChange={set('cached')} options={CACHED} labels={CACHED_LABELS} />
          <span className="text-xs text-grayscale-1100">ms</span>
          <input type="number" min={0} value={f.min_ms} placeholder="min" onChange={e => set('min_ms')(e.target.value)}
            className="h-9 w-24 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm" />
          <input type="number" min={0} value={f.max_ms} placeholder="max" onChange={e => set('max_ms')(e.target.value)}
            className="h-9 w-24 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm" />
          <span className="text-xs text-grayscale-1100">results</span>
          <input type="number" min={0} value={f.min_results} placeholder="min" onChange={e => set('min_results')(e.target.value)}
            className="h-9 w-20 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm" />
          <input type="number" min={0} value={f.max_results} placeholder="max" onChange={e => set('max_results')(e.target.value)}
            className="h-9 w-20 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm" />
          <Select value={range} onChange={pickRange} options={RANGES} labels={RANGE_LABELS} />
          <span className="text-xs text-grayscale-1100">from</span>
          <TextInput type="date" value={f.from} onChange={set('from')} />
          <span className="text-xs text-grayscale-1100">to</span>
          <TextInput type="date" value={f.to} onChange={set('to')} />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-grayscale-1100">sort by</span>
          <Select value={sort?.key || ''} onChange={k => setSort(k ? { key: k, dir: sort?.dir || 'desc' } : null)}
            options={['', ...SORTS]} labels={{ '': 'Newest first (default)', ...SORT_LABELS }} />
          {sort && (
            <Btn small onClick={() => setSort(s => s ? { key: s.key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : s)}
              title="Flip the direction">{sort.dir === 'asc' ? '↑ ascending' : '↓ descending'}</Btn>
          )}
          <Select value={perPage} onChange={setPerPage} options={['25', '50', '100', '200']}
            labels={{ '25': '25 / page', '50': '50 / page', '100': '100 / page', '200': '200 / page' }} />
          <BandLegend />
        </div>
        <TagFilter value={tagF} onChange={v => setTagF(v)} />
        {nActive > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap">
            {active.map(([k, v]) => (
              <Chip key={k} label={FILTER_LABELS[k] || k} value={String(v)} onClear={() => set(k as keyof Filters)('')} />
            ))}
            {tagActive && (
              <Chip label="lifecycle / tags"
                value={describeTagFilter(tagF, k => tagOpts.tagByKey[k]?.label || k).join(' · ')}
                onClear={() => setTagF(EMPTY_TAG_FILTER)} />
            )}
          </div>
        )}
        <p className="text-xs text-grayscale-1100">
          One row per /search/v2 call. The breakdown bar splits the total into understanding ·
          retrieve · rescore · hydrate · other; hover it for every step. Error rows are tinted red,
          cache hits grey (their steps are the original run's). Click a row for the full entry and
          the results the user saw.
        </p>
        {list.error && <Msg kind="error">{list.error}</Msg>}
        {list.loading && !list.data ? <Loading /> : (
          <RowClick onRow={setOpenId}
            className="[&_tbody_tr:has([data-tone=error])]:bg-red-50 [&_tbody_tr:has([data-tone=cache])]:bg-grayscale-200">
            <DataTable rows={rows} columns={columns} labels={LABELS} csvName="search_queries"
              serverSort={sort} onSortChange={onSortChange} maxHeight="40rem"
              empty={nActive ? 'Nothing matches these filters.' : 'No search queries logged yet.'}
              render={{
                created_at: r => (
                  <span data-row-key={r.id} data-tone={r.error ? 'error' : r.cached ? 'cache' : 'plain'}
                    title={`${r.created_at}\n${r.id}`}
                    className={`border-l-2 pl-2 ${r.error ? 'border-red-400' : r.cached ? 'border-grayscale-600' : 'border-transparent'}`}>
                    {fmtDate(r.created_at)}
                  </span>
                ),
                user_email: r => r.user_email || r.user_id ? (
                  <button type="button" className="underline underline-offset-2 text-grayscale-1300"
                    title={`Filter by this user${r.user_id ? ` (${r.user_id})` : ''}`}
                    onClick={() => toggle('user', r.user_email || r.user_id)}>
                    {r.user_email || r.user_id}
                  </button>
                ) : <span className="text-grayscale-1100">anonymous</span>,
                lifecycle_tags: r => (r.user_id || r.user_email) ? <span className="inline-block min-w-[12rem]"><UserTagCells row={r} /></span> : '',
                query: r => (
                  <span className="inline-flex flex-col min-w-0 max-w-[26rem]">
                    <span className={`truncate ${r.error ? 'text-red-700' : 'text-grayscale-1300'}`} title={r.query}>
                      {r.query || <span className="text-grayscale-1100">(empty)</span>}
                    </span>
                    {r.ai_search_query && r.ai_search_query !== r.query && (
                      <span className="text-xs text-grayscale-1100 truncate" title={`AI search query: ${r.ai_search_query}`}>
                        {r.ai_search_query}
                      </span>
                    )}
                    {r.error && <span className="text-xs text-red-600 truncate" title={String(r.error)}>{String(r.error)}</span>}
                  </span>
                ),
                retrieval_mode: r => (
                  <span className="inline-flex items-center gap-1.5" title={`${r.backend || ''}\n${r.retrieval_mode || ''}`}>
                    {familyPill(r.family)}
                    <span>{r.route || '—'}{r.engine ? <span className="text-grayscale-1100"> / {r.engine}</span> : null}</span>
                  </span>
                ),
                total_results: r => num(r.total_results) === null ? '—'
                  : Number(r.total_results) === 0 ? <span className="text-grayscale-1100">0</span>
                    : Number(r.total_results).toLocaleString(),
                total_ms: r => <span className={r.error ? 'text-red-700' : ''}>{fmtMs(r.total_ms)}</span>,
                timing: r => {
                  const { total: t, bands } = rowBands(r);
                  const title = stepsTitle(r.timings) + (fromOriginalRun(r) ? '\n(steps copied from the original run)' : '');
                  return <StackedBar bands={bands} total={t} title={title} className="w-40" />;
                },
                ...Object.fromEntries(STEP_COLS.map(s => [s, (r: Record<string, any>) => (
                  <span className={STEP_PARENT[s] ? 'text-grayscale-1100' : ''}>{fmtMs(r[s])}</span>
                )])),
              }}
              actions={r => <Btn small onClick={() => setOpenId(String(r.id))}>Details</Btn>} />
          </RowClick>
        )}
        {total > 0 && (
          <div className="flex items-center justify-between gap-3 flex-wrap text-xs text-grayscale-1100">
            <span>
              Page {page + 1} of {pages} · showing {rows.length ? page * Number(perPage) + 1 : 0}–
              {page * Number(perPage) + rows.length} of {total.toLocaleString()}
            </span>
            <div className="flex items-center gap-2">
              <Btn small onClick={() => setPage(0)} disabled={page === 0}>« First</Btn>
              <Btn small onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>‹ Prev</Btn>
              <Btn small onClick={() => setPage(p => Math.min(pages - 1, p + 1))} disabled={page >= pages - 1}>Next ›</Btn>
              <Btn small onClick={() => setPage(pages - 1)} disabled={page >= pages - 1}>Last »</Btn>
            </div>
          </div>
        )}
      </Section>

      {openId !== null && (
        <QueryDrawer id={openId} onClose={() => setOpenId(null)} onOpen={setOpenId}
          onSearch={qq => { setOpenId(null); setF(s => ({ ...s, q: qq })); }} />
      )}
    </div>
  );
};

export default QueryExplorer;
